import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { LinkedinAccount, LinkedinAccountStatuses } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiButton from 'features/ui/base-components/pai-button';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  status: LinkedinAccountStatuses | null;
  accountId: string | null;
  linkedinAccounts: LinkedinAccount[];

  handleClose(): void;
}

const AutoSyncWarningDialog: React.FC<Props> = ({ open, status, accountId, linkedinAccounts, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const getStatusText = () => {
    switch (status) {
      case LinkedinAccountStatuses.NOT_SYNCHRONIZED:
        return t('notSynchronizedWarning');
      case LinkedinAccountStatuses.WRONG_CREDENTIALS:
        return t('wrongCredentialsWarning');
      case LinkedinAccountStatuses.TWO_FA:
        return t('twoFaWarning');
      case LinkedinAccountStatuses.WRONG_PIN:
        return t('wrongPinWarning');
      case LinkedinAccountStatuses.CAPTCHA:
        return t('captchaWarning');
      case LinkedinAccountStatuses.TIMED_OUT_CAPTCHA:
        return t('timedOutCaptchaWarning');
      case LinkedinAccountStatuses.CHOOSE_CONTRACT:
        return t('chooseContractWarning');
      case LinkedinAccountStatuses.ONBOARDING_INCOMPLETE:
        return t('onboardingIncompleteWarning');
      case LinkedinAccountStatuses.RECRUITER_PARALLEL_LOGIN:
        return t('recruiterParallelLoginWarning');
      case LinkedinAccountStatuses.FAIR_USE_PAUSE:
        return t('fairUsePauseWarning');
      case LinkedinAccountStatuses.THIRD_PARTY:
      case LinkedinAccountStatuses.THIRD_PARTY_TEXT:
        return t('thirdPartyWarning');
      case LinkedinAccountStatuses.TWO_FA_NOT_ENABLED:
        return t('twoFaNotEnabledWarning');
      default:
        return t('unknownErrorWarning');
    }
  };

  const getLinkedinAccountName = () => {
    const account = linkedinAccounts.find((account) => account._id === accountId);
    return account?.userEmail || '';
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('autoSyncWarningDialogTitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          <Typography className={classes.text}>
            <Trans i18nKey={'autoSyncWarningDialogContent1'} values={{ accountName: getLinkedinAccountName() }}>
              We could not successfully sign in your LinkedIn account <strong>{getLinkedinAccountName()}</strong>.
            </Trans>
          </Typography>
          {status !== LinkedinAccountStatuses.FAIR_USE_PAUSE ? (
            <Typography className={classes.text}>{t('autoSyncWarningDialogContent2')}</Typography>
          ) : null}
          <Typography className={classes.text}>
            <strong>{t('autoSyncWarningDialogContent3')}</strong>
          </Typography>
          <Typography className={cx(classes.text, 'highlight')}>{getStatusText()}</Typography>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={handleClose} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: `linear-gradient(to right, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '16px 16px 24px 16px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
    whiteSpace: 'pre-wrap',
    '&.highlight': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AutoSyncWarningDialog;
